import {
  TextField as MuiTextField,
  TextFieldProps,
  Typography,
} from "@mui/material";
import { ReactElement, useState } from "react";
import {
  FieldValues,
  useController,
  UseControllerProps,
} from "react-hook-form";

import { FlexBox } from "~frontend/components/FlexBox";
import theme, { palette } from "~frontend/theme";

export function RhfCommaSeparatedTextField<T extends FieldValues>(
  props: Omit<TextFieldProps, "onChange" | "value"> &
    UseControllerProps<T> & {
      optional?: boolean;
      bottomLabel?: string;
      characterLimit?: number;
    }
): ReactElement {
  const { field, fieldState } = useController(props);

  const { characterLimit, ...rest } = props;

  const [inputValue, setInputValue] = useState(() =>
    Array.isArray(field.value) ? field.value.join(", ") : field.value || ""
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputValue(value);
  };

  const handleBlur = () => {
    const arrayValue = inputValue
      ? inputValue
          .split(",")
          .map((item: string) => item.trim())
          .filter((item: string) => item.length > 0)
      : [];
    field.onChange(arrayValue);
    field.onBlur();

    setInputValue(arrayValue.join(", "));
  };

  const charsOverLimit =
    characterLimit && inputValue ? inputValue.length - characterLimit : 0;

  return (
    <MuiTextField
      onChange={handleChange}
      onBlur={handleBlur}
      value={inputValue}
      name={field.name}
      inputRef={field.ref}
      error={Boolean(fieldState.error)}
      helperText={
        fieldState.error ? fieldState.error.message : props.bottomLabel
      }
      {...(rest as TextFieldProps)}
      label={
        <FlexBox
          sx={{ width: "100%" }}
          justifyContent="space-between"
          alignItems="center"
        >
          <FlexBox alignItems="center" gap={1}>
            {props.label}
            {props.optional ? <Typography> (optional)</Typography> : ""}
          </FlexBox>
          {characterLimit ? (
            <FlexBox alignItems="flex-end">
              <Typography
                variant="h4Caps"
                component="div"
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: charsOverLimit > 0 ? palette.red : palette.navy,
                }}
              >
                {charsOverLimit > 0
                  ? `${charsOverLimit} character${charsOverLimit > 1 ? "s" : ""} over limit`
                  : `${characterLimit} character limit`}
              </Typography>
            </FlexBox>
          ) : (
            <div />
          )}
        </FlexBox>
      }
      slotProps={{
        inputLabel: {
          sx: { width: "133%", maxWidth: `calc(140% - ${theme.spacing(4)})` },
          shrink: true,
          ...(props.InputLabelProps || props.slotProps?.inputLabel || {}),
        },
        input: {
          ...(props.InputProps || props.slotProps?.input || {}),
        },
      }}
    />
  );
}
